// src/services/aeo/clientDocsWriteTypes.ts
//
// The Docs WRITE contract v1.0 (2 Oct 2026): phases 2 and 3 of the documents
// work, as types. This file is the one source the handoffs (B11 to B28, H20a,
// H30, H31) quote verbatim, the way deliveryTypes.ts is for Delivery. The
// Phase 1 read shapes stay in clientDocsTypes.ts; the B26 addendum to them
// (posts on the client's behalf, question ids, per-code states, the amber
// flag) is written there, additive. The Phase 3 shapes on Delivery items live
// in deliveryTypes.ts (contract v1.3).
//
// Rules of the contract:
// - Every key is present; null means unknown, not measured or not yet.
// - Every staff mutation answers the full document (`DocumentDetail`) as it
//   stands after the write, so a screen never patches its own copy.
// - Oura checks the work, people do it: every write names the person who
//   clicked (`by`, from the token). No route posts, shares, sends or files on
//   its own, and no field records Oura as the one acting.
// - Client-facing output never goes past Draft without a named person: every
//   email is sent by a named person, the "Live since" reply is drafted for a
//   person to post, and publishing to a client site is a person's click.
// - Refusals carry their slug in `errors[0]` (DocsWriteErrorCode below), with
//   the HTTP status shown beside each.

import type { DocAccount, DocFill, DocSide, DocumentDetail, MerchantDocument, StaffDocument } from "./clientDocsTypes";

/* ------------------------------------------------------------------------- *
 * B11: write access as oura@ (Drive and Docs)
 * ------------------------------------------------------------------------- */

/**
 * `GET /documents/connection` gains `write` (additive; absent = the server
 * does not send it yet, which reads as "Read only").
 */
export interface DocsWriteStatus {
    /** "oura@ourteam.ai"; null = not configured. */
    actingAs: string | null;
    /** Drive scope held by the delegation: read only, or read and write. null = not configured. */
    drive: "read" | "write" | null;
    /** Docs scope: "write" once the Docs API scope is delegated; null = not delegated. */
    docs: "write" | null;
    /** Last time a write-scoped token was minted as oura@; null = never. */
    lastWriteCheckAt: string | null;
    /** The server's sentence, e.g. "Writes client docs as oura@ourteam.ai. Checked 4 min ago." */
    detail: string;
}

/* ------------------------------------------------------------------------- *
 * B12: create a doc
 * ------------------------------------------------------------------------- */

/**
 * template: copy the doc type's template; skill: run its skill (B14); link: file an existing Google Doc;
 * convert: make a Google Doc from a Word file; blank: an empty Google Doc, for a custom doc type only
 * (ourteam-backend#6517).
 */
export type CreateDocKind = "template" | "skill" | "link" | "convert" | "blank";

/** `POST /businesses/{id}/documents`. */
export interface CreateDocIn {
    kind: CreateDocKind;
    /** "Answer Plan": a catalogue doc type (`DOC_EXPECTED_CURRENT`). */
    docType: string;
    /** Base codes, ["S1","S3","B1","G4"]. */
    codes: string[];
    /** "MK" for a per-outlet doc (S2). */
    outlet?: string | null;
    /** Line 1 of the doc; null = docType plus codes. */
    title?: string | null;
    /** The Drive folder to create in; null = the client's linked folder. Refused when shared with the client. */
    folderId?: string | null;
    /** kind "link": the Google Doc's link. */
    linkUrl?: string | null;
    /** kind "convert": the Word file's Drive id, a file in the client's folder. */
    fileId?: string | null;
    /** kind "skill": a note for a re-run ("Shorter answers, add the halal line"). */
    note?: string | null;
    /**
     * A doc type staff name themselves ("Treatment Menu", ourteam-backend#6516). Set, it replaces
     * `docType`; its codes must be in the client's plan and its outlet, if any, one of theirs.
     */
    customType?: string | null;
    /**
     * The date in the file name, `YYYY-MM-DD` (ourteam-backend#6556); absent = today. A doc made
     * before Oura keeps its own day. After today is refused `name_date_in_future`.
     */
    nameDate?: string | null;
}

/**
 * What Create answers. The doc is never shared on create: `document.sharing.clientCanSee`
 * is false, and sharing is its own step (B17) after a person reads it.
 */
export interface CreateDocResult {
    document: StaffDocument;
    /** kind "skill": the run record. null for every other kind. */
    run: SkillRun | null;
}

/**
 * `POST /documents/{id}/fill`: the same doc, filled again from its template
 * after a fill that failed or never finished (#4185). Answers at once with the
 * doc and `filling: true`; the fill itself takes 45 to 120 seconds.
 */
export interface RerunFillResult {
    document: StaffDocument;
    filling: boolean;
    fill: DocFill | null;
}

/**
 * `GET /documents/templates?custom=true`: what Create offers, 1 per catalogue doc type, then the
 * Custom row (ourteam-backend#6516): `custom: true`, no codes of its own, staff name the type.
 */
export interface DocTemplate {
    docType: string;
    codes: string[];
    perOutlet: boolean;
    eachMonth: boolean;
    /** The template's Drive id; null = no template yet, so Create from template is refused. */
    templateFileId: string | null;
    /** Whether a skill is added for this doc type (B13); null for the Custom row. */
    skill: SkillStatus | null;
    /** The Custom row: staff type the doc type. Absent from a backend before it. */
    custom?: boolean;
    /** The ways this row can be made, in the order to offer them. Absent from a backend before it. */
    kinds?: CreateDocKind[];
}

/** `GET /businesses/{id}/docs-folder/targets`: where a doc can be created. */
export interface CreateTarget {
    folderId: string;
    /** "Nyonya Table / Shared with Siew Ling". */
    path: string;
    /** The linked folder itself. */
    root: boolean;
    /** Any client address can open it (inherited by every file made in it). Create refuses here. */
    sharedWithClient: boolean;
    /** The client addresses it is shared with; [] when none. */
    clientEmails: string[];
}

/* ------------------------------------------------------------------------- *
 * B13 and B14: the Skill library (placeholders tonight: no runner)
 * ------------------------------------------------------------------------- */

export type SkillStatus = "none" | "added";

/** One fact Oura will send a skill. A skill runs with no internet, so Oura sends it everything. */
export interface SkillInput {
    /** "contract", "questions", "scan", "onboarding_form", "knowledge", "previous_doc". */
    key: string;
    /** "The client's 50 questions". */
    label: string;
    /** Where Oura reads it: "Visibility", "Onboarding form", "Knowledge". */
    from: string;
}

/** A spend cap. Placeholders tonight: `spentUsd` stays null until a real run is measured. */
export interface SpendCap {
    /** 100 for skill runs, 20 for summaries (YW's defaults, 2 Oct 2026). */
    monthCapUsd: number;
    /** 80 and 16: the alert line. */
    alertAtUsd: number;
    /** This month's spend; null = not measured (no run yet). Never shown as 0. */
    spentUsd: number | null;
    /** True once the cap is reached: runs stop and say so. */
    stopped: boolean;
}

/** `GET /oura/skills`: 1 slot per catalogue doc type. */
export interface SkillSlot {
    docType: string;
    codes: string[];
    status: SkillStatus;
    /** The skill's version in its Anthropic workspace; null until added. */
    version: string | null;
    addedAt: string | null;
    addedBy: string | null;
    /** What Oura will send it. */
    inputs: SkillInput[];
    /** Cost of one run; null until a real run is measured (placeholder). */
    costPerRunUsd: number | null;
    /** The last 5 runs for this doc type, across every client, newest first. */
    runs: SkillRun[];
}

export interface SkillLibrary {
    slots: SkillSlot[];
    cap: SpendCap;
}

/** The longest note a run can carry (`note_too_long` over it): the first run's and a re-run's. */
export const SKILL_NOTE_MAX = 500;

/**
 * A run record (B14). Create with kind `skill` starts one (`run.status` is
 * `running`, and the draft lands in the doc when it ends), and
 * `POST /documents/{id}/skill-runs` starts a re-run into the next version
 * (ourteam-backend#5268). The Skill library's slot lists the last 5 per doc type.
 */
export interface SkillRun {
    id: string;
    docType: string;
    /** The doc the run wrote into; null while it runs or when it failed. */
    documentId: string | null;
    skillVersion: string | null;
    /** The person who clicked Create or Re-run. */
    startedBy: string;
    startedAt: string;
    finishedAt: string | null;
    status: "running" | "done" | "failed" | "stopped_at_cap";
    /** A re-run's note; null for a first run. */
    note: string | null;
    /** null = not measured. */
    costUsd: number | null;
    inputTokens: number | null;
    outputTokens: number | null;
    /** The server's sentence when it failed or stopped. */
    error: string | null;
}

/* ------------------------------------------------------------------------- *
 * B15: questions
 * ------------------------------------------------------------------------- */

/** agree: Agree or Change; change: what to change; choice: pick an option; text: a written answer; file: send a file. */
export type QuestionKind = "agree" | "change" | "choice" | "text" | "file";

/**
 * A question Oura keeps for a doc. Drafted in Oura, then "Send to the doc"
 * posts the numbered list into the doc's body and 1 comment per question,
 * keeping the comment id, so the thread carries `questionId` (B26).
 */
export interface DocQuestion {
    id: string;
    /** "S2-MK"; null = the doc's codes apply. */
    code: string | null;
    kind: QuestionKind;
    text: string;
    /** For "choice": 2 or more; [] for every other kind. */
    options: string[];
    /** The question's number in the doc's list; null until sent. */
    number: number | null;
    /** The Drive comment id; null until sent. */
    commentId: string | null;
    /** The doc thread it became; null until sent. */
    threadId: string | null;
    state: "draft" | "sent";
    createdBy: string;
    createdAt: string;
    sentAt: string | null;
}

/** `POST /documents/{id}/questions`. */
export interface QuestionIn {
    code?: string | null;
    kind: QuestionKind;
    text: string;
    options?: string[];
}

/** `PATCH /documents/{id}/questions/{questionId}`. A sent question's comment is edited in the doc too. */
export type QuestionPatchIn = Partial<QuestionIn>;

/**
 * Added for lane 3 (2 Oct 2026, additive; route 8 as `DocumentDetail.questionCheck`):
 * the 3 counts that must agree after "Send to the doc". `inOura` is the sent
 * questions on Oura's record; `inList` the lines in the doc's numbered
 * "Your questions" list, read back from the body; `comments` the comments in
 * the doc that carry a question id. null = not read back yet.
 */
export interface QuestionCheck {
    inOura: number;
    inList: number | null;
    comments: number | null;
}

/* ------------------------------------------------------------------------- *
 * B16: edits Oura makes, and Rename
 * ------------------------------------------------------------------------- */

/**
 * `PATCH /documents/{id}/lines`: only Oura's own lines. Line 1 is the title
 * ("Answer Plan (S1, S3, B1, G4)"), line 2 the version line
 * ("Nyonya Table · v1.1"). Nothing else in the body is touched.
 */
export interface DocLinesIn {
    titleLine?: string;
    versionLine?: string;
}

/**
 * `POST /documents/{id}/rename`. With no `name`, the server writes the decided
 * format from the doc's facts:
 * `(<codes>) <Client>_<Doc>_<Location>_<DD MMMM YYYY>_<ver>`, codes "S2-S4"
 * (`docNameOf` in clientDocsRules.ts).
 */
export interface RenameIn {
    name?: string | null;
}

/* ------------------------------------------------------------------------- *
 * B17: share with the client
 * ------------------------------------------------------------------------- */

/** `GET /documents/{id}/share-check`: what must be fixed before the client can open it. */
export interface ShareCheck {
    /** True when no red item is left. */
    ok: boolean;
    items: ShareCheckItem[];
    /** The client addresses Oura will share to, comment-only (from onboarding's access contact and approver). */
    suggestedEmails: string[];
}
/** The checks this frontend knows by name, each with its own wording and fix. */
export type ShareCheckCode = "link_sharing" | "edit_access" | "internal_notes";

export interface ShareCheckItem {
    /**
     * A ShareCheckCode, or a code added since this frontend shipped.
     *
     * The server grows this list without a release here -- `template_leftovers`
     * is next (ourteam-backend#5519) -- and an unknown code already holds the
     * share, because `ok` is false. So it is typed open and SHOWN: a share
     * refused "until the checks in red are fixed" with nothing in red is a dead
     * end (#4051).
     */
    code: ShareCheckCode | (string & {});
    /** The fix in words: "Turn link sharing off". */
    message: string;
    detail: string[] | null;
    /**
     * Added for lane 3 (2 Oct 2026, additive): "fix" blocks the share until
     * it is fixed (link sharing, edit access: `fixShareCheck` fixes either on
     * a person's click); "warn" is said and does not block (our internal
     * notes, deleted by a person in the doc). Absent = "fix".
     */
    level?: "fix" | "warn";
}

/** Added for lane 3: what `POST /documents/{id}/share-check/fix` can fix. It answers the new ShareCheck. */
export type ShareFixCode = Extract<ShareCheckCode, "link_sharing" | "edit_access">;
export interface ShareFixIn {
    code: ShareFixCode;
}

/** `POST /documents/{id}/share`: comment-only to these addresses. Refused while the check has a red item. */
export interface ShareIn {
    emails: string[];
}

/**
 * Sharing sets `sharing.sharedAt` and rings the client's bell only: no email
 * from Oura or Google. The review email is its own step (B18), sent by a
 * named person.
 */
export interface ShareNotice {
    audience: "client";
    channel: "bell";
}

/* ------------------------------------------------------------------------- *
 * B18: review email and chaser, through onboarding's send route
 * ------------------------------------------------------------------------- */

/** 2 more kinds on `POST /businesses/{id}/onboarding/messages` (onboarding B3). */
export type DocMessageKind = "doc_review" | "doc_chaser";

export interface DocMessagePerson {
    name: string;
    email: string;
}

/** The body onboarding's send route takes for the doc kinds. Mailed from Angel at oura@, reply-to oura@, BCC oura@. */
export interface DocMessageIn {
    kind: DocMessageKind;
    to: DocMessagePerson[];
    cc: DocMessagePerson[];
    subject: string;
    body: string;
    /** The docs the email links to, in order. */
    documentIds: string[];
    /** Questions waiting on the client across those docs when the email was written. */
    questionCount: number;
    /** YYYY-MM-DD, the earliest reply-by of those docs; null = none set. */
    replyBy: string | null;
}

/** `GET /businesses/{id}/documents/message-draft?kind=&documentIds=`: the server's ready draft. */
export type DocMessageDraft = Omit<DocMessageIn, "kind"> & { kind: DocMessageKind };

/** The kinds the doc's sent list holds: the 2 above, and the client email for a finished doc (ourteam-backend#6700). */
export type SentDocMessageKind = DocMessageKind | "doc_delivery";

/** What the send route answers for the doc kinds. */
export interface SentDocMessage extends Omit<DocMessageIn, "kind"> {
    kind: SentDocMessageKind;
    id: string;
    sentAt: string;
    /** The named person who clicked Send. Never Oura. */
    sentBy: string;
    /** A client email (doc_delivery) only: the HTML it went out as (ourteam-backend#6710). */
    html?: string;
}

/* ------------------------------------------------------------------------- *
 * The client email for a finished doc (epic ourteam-backend#6697)
 * ------------------------------------------------------------------------- */

/** What the email is for: its opening and subject, and what Oura looks for in the doc. */
export type ClientEmailPurpose = "review" | "monthly_report" | "follow_up";

/** One of the guide's checks, run by the server on every draft and preview. A failed `block` one stops the send. */
export interface ClientEmailCheck {
    id: string;
    ok: boolean;
    level: "block" | "warn";
    text: string;
}

/** `POST /documents/{id}/client-email/draft`: Oura's draft, the HTML the client gets, and its checks. */
export interface ClientEmailDraft {
    draftId: string;
    purpose: ClientEmailPurpose;
    madeFrom: string | null;
    madeAt: string | null;
    to: DocMessagePerson[];
    cc: DocMessagePerson[];
    subject: string;
    /** The light markup staff edit: **bold**, [text](https://…), "- " and "1. " lists. */
    body: string;
    /** `body` as the client's inbox shows it: the same HTML the send hands to the mail provider. */
    html: string;
    checks: ClientEmailCheck[];
    /** Highlights or asks Oura dropped because the doc does not say them. */
    dropped: number;
    label: "AI";
}

/** `GET /documents/{id}/client-email`: what the email view opens on. Never a model call. */
export interface ClientEmailState {
    /** Staff marked it Done and it is shared with the client. */
    eligible: boolean;
    refusal: { code: string; message: string } | null;
    purposes: { key: ClientEmailPurpose; label: string }[];
    /** The one the email starts on: follow up once one was sent, a monthly report for a Monthly Performance Report (or an older Monthly Update). */
    purpose: ClientEmailPurpose;
    /** The draft kept for the doc as it stands, for `purpose`; null = none yet (making one is a paid call). */
    draft: ClientEmailDraft | null;
    /**
     * The email as staff saved it: kept until it is sent or they go back to Oura's draft. When
     * present the dialog opens on it, on its `purpose`, and never makes a new draft.
     * Absent on an older backend.
     */
    saved?: ClientEmailSaved | null;
    last: SentDocMessage | null;
    cap: SpendCap;
}

/** `PUT /documents/{id}/client-email/saved`: the email as staff left it, rendered and checked. */
export interface ClientEmailSaved {
    purpose: ClientEmailPurpose;
    to: DocMessagePerson[];
    cc: DocMessagePerson[];
    subject: string;
    body: string;
    html: string;
    checks: ClientEmailCheck[];
    savedBy: string | null;
    savedAt: string | null;
}

/** `POST /businesses/{id}/client-email/multi/draft`: one email for several docs. Never kept. */
export interface MultiClientEmailDraft {
    purpose: ClientEmailPurpose;
    to: DocMessagePerson[];
    cc: DocMessagePerson[];
    subject: string;
    body: string;
    html: string;
    /** The docs it names, in the order picked. */
    documents: { id: string; heading: string; fileType: string }[];
    /** Docs Oura could not read (a PDF, or a refused read): their highlights are not in it. */
    unread: string[];
}

/** `POST /documents/{id}/client-email/preview`: the edited email, as the client gets it. */
export interface ClientEmailPreview {
    html: string;
    checks: ClientEmailCheck[];
}

/** `POST /documents/{id}/client-email/send`. */
export interface ClientEmailSendIn {
    purpose: ClientEmailPurpose;
    to: DocMessagePerson[];
    cc: DocMessagePerson[];
    subject: string;
    body: string;
    draftId: string | null;
    /** One per press of Send: the same key again answers the email already sent, never a second. */
    sendKey: string;
    /** Staff checked the Oura inbox after a send whose outcome is unknown, and send over it. */
    resendUnknown?: boolean;
}

/* ------------------------------------------------------------------------- *
 * B19: client answers in Oura, posted into the doc thread
 * ------------------------------------------------------------------------- */

export type ClientAnswerKind = "agree" | "change" | "option" | "text" | "file";

/** `POST /businesses/mine/documents/{id}/threads/{threadId}/answer` (merchant token). */
export interface ClientAnswerIn {
    kind: ClientAnswerKind;
    /** change: what to change (required). */
    note?: string;
    /** option: one of the question's options. */
    option?: string;
    /** text: the answer. */
    text?: string;
    /** file: from `POST /businesses/mine/documents/files`. Lane 5 (additive): text may carry one too, a file sent with the words. */
    fileKey?: string;
    /**
     * Added for the usability round (2 Oct 2026, additive): every file of a
     * question that asks for several ("Send 3 photos"). `fileKey` stays the
     * first, so a server that reads only it still gets one.
     */
    fileKeys?: string[];
}

/** `POST /businesses/mine/documents/files` (multipart): stored in the client's Drive folder, "Files from <client>". */
export interface ClientFile {
    fileKey: string;
    name: string;
    /** The Drive link staff open. */
    url: string;
    folderPath: string;
}

/**
 * How an answer lands in the doc: a reply by oura@ on the client's behalf,
 * which Oura reads back as the client's (B26): `side: "client"`,
 * `account: "oura"`, `onBehalfOf: <the client's name>`, `where: "oura"`.
 * Agree also resolves the thread on the client's behalf: the approval of
 * record. Change leaves it open, so it reads Needs us. An answer never
 * creates a pack item.
 */
export interface OnBehalfPost {
    side: Extract<DocSide, "client">;
    account: Extract<DocAccount, "oura">;
    onBehalfOf: string;
    via: AnswerVia;
}

/** Where the client's words came from: answered in Oura, or logged by staff from an email, a WhatsApp message or a call (B22). */
export type AnswerVia = "oura" | "email" | "whatsapp" | "call";

/** The merchant's answer route answers their doc as they now see it. */
export type ClientAnswerResult = MerchantDocument;

/* ------------------------------------------------------------------------- *
 * B20: staff reply, resolve, reopen; Resolve N agreed
 * ------------------------------------------------------------------------- */

/** `POST /documents/{id}/threads/{threadId}/reply`: posted by oura@, signed with the staff member's name. */
export interface StaffReplyIn {
    text: string;
}

/**
 * `POST /documents/{id}/threads/{threadId}/resolve`. When the client's last
 * word is not an agreement, the first call is refused `client_not_agreed`
 * and the screen asks again; the second click sends `confirmNotAgreed`.
 * The resolve is then kept, with the amber flag.
 */
export interface ResolveIn {
    confirmNotAgreed?: boolean;
}

/** `POST /documents/{id}/threads/{threadId}/reopen`. */
export interface ReopenIn {
    reason?: string;
}

/** `POST /documents/{id}/resolve-agreed`: resolve every open question whose client's last word is an agreement (or only these). */
export interface ResolveAgreedIn {
    threadIds?: string[];
}

/* ------------------------------------------------------------------------- *
 * B21: fix our own posts, and the one-off placeholder clean-up
 * ------------------------------------------------------------------------- */

/** `PATCH /documents/{id}/threads/{threadId}/posts/{postId}`; `DELETE` the same path. Only posts Oura wrote. */
export interface PostPatchIn {
    text: string;
}

/** `POST /businesses/{id}/documents/placeholders/clean-up`: once, as no-reply@ for its old placeholders. */
export interface PlaceholderCleanUpIn {
    /** Default true: count only. */
    dryRun?: boolean;
}
export interface PlaceholderCleanUp {
    dryRun: boolean;
    found: number;
    removed: number;
    documents: { documentId: string; title: string; count: number }[];
}

/* ------------------------------------------------------------------------- *
 * B22: an answer by email, WhatsApp or a call, logged into the thread
 * ------------------------------------------------------------------------- */

/** `POST /documents/{id}/threads/{threadId}/log`: posted on the client's behalf, with who logged it. */
export interface LogAnswerIn {
    via: Exclude<AnswerVia, "oura">;
    /** The client's words, as they said them. */
    text: string;
    /** When they said it, ISO; not in the future. */
    saidAt: string;
    /** "agree" when their words agree, so Resolve can carry the agreement; null = neither. */
    answer?: "agree" | "change" | null;
}

/* ------------------------------------------------------------------------- *
 * B23: the approval record
 * ------------------------------------------------------------------------- */

/** `GET /documents/{id}/approval-record`: 1 row per resolved question. */
export interface ApprovalRecord {
    documentId: string;
    rows: ApprovalRecordRow[];
    /** Rows with the amber flag. */
    unagreed: number;
}
export interface ApprovalRecordRow {
    threadId: string;
    questionId: string | null;
    number: number | null;
    question: string;
    /** The client's words that agreed, as posted; null = none (the flag says so). */
    clientWords: string | null;
    /** How those words arrived. */
    clientVia: AnswerVia | "doc" | null;
    resolvedBy: { name: string; side: DocSide };
    /** Resolved from Oura, or in the doc itself. */
    where: "in_oura" | "in_doc";
    at: string;
    /** The doc's version when it was resolved ("1.0"); null = unknown. */
    docVersion: string | null;
    /** "unagreed": resolved without the client's agreement. */
    flag: "unagreed" | null;
}

/* ------------------------------------------------------------------------- *
 * B24: file the approved PDF
 * ------------------------------------------------------------------------- */

/**
 * `POST /documents/{id}/file-approved`: a named person files the PDF of the
 * approved version to the client's Reports, and on the same click the doc
 * moves to the next version (name, version line). The client hears in the
 * bell only.
 */
export interface FileApprovedResult {
    document: DocumentDetail;
    report: ApprovedReport;
}
export interface ApprovedReport {
    id: string;
    kind: "doc_approved";
    documentId: string;
    /** "Answer Plan (S1, S3, B1, G4), approved" ("…, approved v1.0" on a filing from before #4874). */
    title: string;
    /** The PDF's file name, in the decided format. */
    fileName: string;
    url: string;
    /** The version the PDF is of; null on a doc with no version (#4874). */
    version: string | null;
    /** The version the doc moved to; the same as `version` since #4874, when filing stopped moving the doc on. */
    nextVersion: string | null;
    /**
     * The doc's latest filing, with nothing come into the doc since: the backend's
     * one answer to "is this approval filed" (ourteam-backend#6803). Absent from an
     * older backend, which compares versions instead (`filedForVersion`).
     */
    current?: boolean;
    filedAt: string;
    /** The named person who clicked. */
    filedBy: string;
    /**
     * Added for lane 5 (2 Oct 2026, additive; H31's "approved PDF on the
     * deliverable"): the codes and outlet the doc covered when it was filed,
     * so Delivery can show the PDF on each deliverable it clears. Absent =
     * the server does not send them yet.
     */
    codes?: string[];
    outlet?: string | null;
}

/* ------------------------------------------------------------------------- *
 * Lane 5 (2 Oct 2026, additive on route 12's MerchantDocument): what the
 * client answered. The approved PDFs reach their Reports as report rows of
 * kind "doc_approved" (B24), never through route 12.
 * ------------------------------------------------------------------------- */

/**
 * A question the client has answered and that waits on no one but us, or is
 * approved: what they said and when, so their list can show it. A question
 * we replied to again is back in `openQuestions` instead.
 */
export interface MerchantAnswered {
    /** The thread. */
    id: string;
    questionId: string | null;
    /** Its number in the doc's list; null for a question Oura did not number. */
    number: number | null;
    /** The question, without its "[S2-MK]" tag. */
    text: string;
    /** Their answer kind from Oura's answer screen; null for words written in the doc. */
    answer: ClientAnswerKind | null;
    /** Their words as posted ("Agreed.", "Change: ...", "Chose: ..."); "" for a resolve with no words. */
    words: string;
    /** A file they sent with it, stored in their Drive folder. */
    file: { name: string; url: string } | null;
    /** When they answered (for a logged answer, when they said it). */
    at: string;
    /** Answered in Oura, in the doc, or logged by us from an email, WhatsApp or a call. */
    via: AnswerVia | "doc";
    /** approved: agreed (resolved with their agreement); with_us: we move next. */
    state: "approved" | "with_us";
}

/* ------------------------------------------------------------------------- *
 * B25: the AI summary (staff only, labelled AI)
 * ------------------------------------------------------------------------- */

/**
 * `GET /documents/{id}/summary` (null = none made); `POST` makes one, only
 * when staff open a doc that changed since the last. It never sets a state
 * and never reaches the client.
 */
export interface DocSummary {
    documentId: string;
    /** 1 line: where the doc stands. */
    line: string;
    openPoints: string[];
    /** Possible mistakes it noticed (a price that differs from the menu, a name spelt 2 ways). */
    mistakes: string[];
    nextStep: string;
    madeAt: string;
    /** The doc's modifiedAt the summary was made from; older than the doc's = stale. */
    madeFrom: string | null;
    label: "AI";
    cap: SpendCap;
}

/* ------------------------------------------------------------------------- *
 * Refusals
 * ------------------------------------------------------------------------- */

/** The write routes' refusal slugs, in `errors[0]`. Status beside each. */
export type DocsWriteErrorCode =
    | "write_not_configured" // 424: oura@ has no Drive and Docs write yet
    | "folder_shared_with_client" // 409: Create in a folder the client can open
    | "doc_exists_unshared" // 409: a doc already covers the codes and is not shared (`existingId`)
    | "doc_type_unknown" // 400
    | "template_missing" // 409: no template for the doc type
    | "skill_not_added" // 409: no skill for the doc type yet
    | "skill_runner_not_configured" // 424: skill runs are not set up on this server (ourteam-backend#5268)
    | "skill_cap_reached" // 429
    | "link_not_a_doc" // 400
    | "file_not_word" // 400
    | "question_text_required" // 400
    | "question_options_required" // 400: a choice needs 2 or more
    | "questions_none_to_send" // 409
    | "question_answered" // 409: a sent question the client answered stays
    | "name_format_invalid" // 400
    | "line_under_open_comment" // 409: an open comment sits on the title or version line (lane 3)
    | "share_check_failed" // 409: a red item is left
    | "share_emails_required" // 400
    | "email_invalid" // 400
    | "not_shared" // 409: an email or chaser about a doc the client cannot open
    | "message_empty" // 400
    | "answer_invalid" // 400
    | "option_unknown" // 400
    | "thread_resolved" // 409: an answer to a resolved question
    | "thread_open" // 409: reopen a thread that is open
    | "client_not_agreed" // 409: resolve without the client's agreement, needs confirmNotAgreed
    | "not_our_post" // 409: only Oura's own posts can be changed
    | "said_at_in_future" // 400
    | "not_approved" // 409: File the approved PDF before every question is approved
    | "already_filed" // 409: this version's PDF is filed and nothing changed since
    | "summary_cap_reached" // 429
    | "summary_failed" // 502: the model answered, but not in a way we could read
    | "fill_not_started" // 409: no fill was ever started for this doc, so there is nothing to run again
    | "fill_in_progress" // 409: Oura is filling it now
    | "fill_done" // 409 from an older backend; since ourteam-backend#6803 a filled doc is filled again in place
    | "shared_with_client" // 409: the client can open it, so Oura will not rewrite it
    | "fill_state_unavailable" // 503: the run could not be recorded, so it was not started
    | "fill_queue_full" // 429: too many fills at once
    // The client email for a finished doc (ourteam-backend#6697).
    | "not_done" // 409: staff have not marked the doc Done
    | "not_client_doc" // 409: not a client Google Doc in its folder
    | "cap_reached" // 429: AI summaries and client emails reached this month's cap
    | "model_unset" // 503: the AI prices are not set, so nothing is spent
    | "doc_unreadable" // 409: Oura could not read the doc's text
    | "model_failed" // 502
    | "check_failed" // 400: a blocking check fails
    | "send_key_required" // 400
    | "send_in_progress" // 409: another send of this doc is on its way
    | "send_unconfirmed" // 409 or 502: a send whose outcome is not known
    | "no_test_inbox"; // 409: no internal test inbox is set, so no test was sent

export const DOCS_WRITE_ERROR_CODES: readonly DocsWriteErrorCode[] = [
    "write_not_configured",
    "folder_shared_with_client",
    "doc_exists_unshared",
    "doc_type_unknown",
    "template_missing",
    "skill_not_added",
    "skill_runner_not_configured",
    "skill_cap_reached",
    "link_not_a_doc",
    "file_not_word",
    "question_text_required",
    "question_options_required",
    "questions_none_to_send",
    "question_answered",
    "name_format_invalid",
    "line_under_open_comment",
    "share_check_failed",
    "share_emails_required",
    "email_invalid",
    "not_shared",
    "message_empty",
    "answer_invalid",
    "option_unknown",
    "thread_resolved",
    "thread_open",
    "client_not_agreed",
    "not_our_post",
    "said_at_in_future",
    "not_approved",
    "already_filed",
    "summary_cap_reached",
    "summary_failed",
    "fill_not_started",
    "fill_in_progress",
    "fill_done",
    "shared_with_client",
    "fill_state_unavailable",
    "fill_queue_full",
    "not_done",
    "not_client_doc",
    "cap_reached",
    "model_unset",
    "doc_unreadable",
    "model_failed",
    "check_failed",
    "send_key_required",
    "send_in_progress",
    "send_unconfirmed",
    "no_test_inbox",
];

/** The sentence a screen shows for each refusal when the server sends none. */
export const DOCS_WRITE_ERROR_MESSAGE: Record<DocsWriteErrorCode, string> = {
    write_not_configured: "Oura's Google account can't write to Google Docs yet.",
    folder_shared_with_client: "The client can open this folder, so a new doc would be visible before we read it. Pick a folder they can't open.",
    doc_exists_unshared: "A doc for these codes is already in the folder and not shared. Read it, then share it.",
    doc_type_unknown: "Oura doesn't know that doc type.",
    template_missing: "There is no template for this doc type yet.",
    skill_not_added: "No skill is added for this doc type yet.",
    // The server sends these with their reason and, for the cap, the date they start again;
    // these stand in only when it sends none. (`note_too_long` is an older code with its own
    // fallback; a skill note's refusal always carries the server's sentence.)
    skill_runner_not_configured: "Skill runs are not set up on this server yet.",
    skill_cap_reached: "Skill runs have reached this month's cap.",
    link_not_a_doc: "That isn't a link to a Google Doc.",
    file_not_word: "Only a Word file can be converted.",
    question_text_required: "Write the question first.",
    question_options_required: "Give at least 2 options.",
    questions_none_to_send: "There are no new questions to send.",
    question_answered: "The client has answered this question, so it stays.",
    name_format_invalid: "The name doesn't follow the decided format, like (S2-S4) The Lush Clinic_GBP Pack_Mont Kiara_06 October 2026_1.0.",
    line_under_open_comment: "An open comment sits on this line. Oura leaves it until the comment is resolved.",
    share_check_failed: "Fix the red items first.",
    share_emails_required: "Add the client's email.",
    email_invalid: "That isn't an email address.",
    not_shared: "Share the doc with the client first.",
    message_empty: "Write the email first.",
    answer_invalid: "That answer is missing something.",
    option_unknown: "Pick one of the options.",
    thread_resolved: "This question is already resolved.",
    thread_open: "This question is already open.",
    client_not_agreed: "The client hasn't agreed to this. Resolve it anyway?",
    not_our_post: "Only our own posts can be changed.",
    said_at_in_future: "Pick a time that has passed.",
    not_approved: "Every question needs the client's yes first.",
    already_filed: "This approval is already filed.",
    summary_cap_reached: "Summaries have reached this month's cap.",
    // The server sends the sentence that says whether the call still counted
    // against the cap; this stands in only when it sends none.
    summary_failed: "The summary could not be made.",
    fill_not_started: "Oura never started filling this doc from a template, so there is nothing to run again.",
    fill_in_progress: "Oura is filling this doc now.",
    fill_done: "This doc is already filled.",
    shared_with_client: "The client can open this doc, so Oura will not rewrite it. Stop sharing it first.",
    fill_state_unavailable: "Oura could not record this fill, so it did not start it. Try again.",
    fill_queue_full: "Oura is filling several docs right now. Try again in a minute.",
    not_done: "Mark the doc Done first.",
    not_client_doc: "Only a client's Google Doc gets a client email.",
    cap_reached: "AI summaries and client emails have reached this month's cap.",
    model_unset: "The AI prices are not set on this server, so Oura can't write the email.",
    doc_unreadable: "Oura could not read the doc's text.",
    model_failed: "Oura could not write the email.",
    check_failed: "A check in red is not true yet.",
    send_key_required: "Send it again from the email view.",
    send_in_progress: "This email is already on its way to the client.",
    send_unconfirmed: "It is not known whether the email went. Check the copy in the Oura inbox first.",
    no_test_inbox: "No test inbox is set on this server, so no test was sent.",
};
